import { AiDocumentationErrorCode } from '../enums/ai-documentation-error-code.enum.js';
import { AiDraftStatus } from '../enums/ai-draft-status.enum.js';
import { DictationFailedStep } from '../enums/dictation-failed-step.enum.js';
import { DictationStatus } from '../enums/dictation-status.enum.js';
import { RegenerationMode } from '../enums/regeneration-mode.enum.js';
const IN_FLIGHT_DICTATION = new Set([
    DictationStatus.UPLOADED,
    DictationStatus.NORMALIZING,
    DictationStatus.TRANSCRIBING,
]);
const IN_FLIGHT_DRAFT = new Set([
    AiDraftStatus.PENDING,
    AiDraftStatus.GENERATING,
]);
/**
 * Why `mode` cannot run now (Spec § 3.2-5), as the 409 code the API answers
 * with, or null when it can. The server enforces it under the dictation row
 * lock; the UI reads it to decide what to offer.
 *
 * - `SAVED`: regeneration after saving goes through the documentation item
 *   (Spec § 3.2-4), not through the dictation.
 * - A run in flight (no transcript yet, or the newest draft still being
 *   generated) is never overtaken.
 * - A failed normalization left no canonical audio to start from; a failed
 *   transcription only recovers from the audio, so `draft` needs the
 *   dictation `TRANSCRIBED`; without the audio it has nothing left.
 * - `transcript` needs the audio. `AUDIO_NO_LONGER_AVAILABLE` therefore only
 *   comes with a `TRANSCRIBED` dictation, whose `draft` mode still works.
 */
export const regenerationBlocker = (state, mode) => {
    const { dictationStatus, failedStep, audioAvailable, latestDraftStatus } = state;
    if (dictationStatus === DictationStatus.SAVED) {
        return AiDocumentationErrorCode.DICTATION_ALREADY_SAVED;
    }
    if (dictationStatus === DictationStatus.DISCARDED) {
        return AiDocumentationErrorCode.NOTHING_TO_REGENERATE;
    }
    if (IN_FLIGHT_DICTATION.has(dictationStatus)) {
        return AiDocumentationErrorCode.RUN_IN_PROGRESS;
    }
    if (latestDraftStatus !== null && IN_FLIGHT_DRAFT.has(latestDraftStatus)) {
        return AiDocumentationErrorCode.RUN_IN_PROGRESS;
    }
    if (dictationStatus === DictationStatus.FAILED &&
        (failedStep !== DictationFailedStep.TRANSCRIBE || !audioAvailable)) {
        return AiDocumentationErrorCode.NOTHING_TO_REGENERATE;
    }
    if (mode === RegenerationMode.DRAFT) {
        return dictationStatus === DictationStatus.TRANSCRIBED
            ? null
            : AiDocumentationErrorCode.NOTHING_TO_REGENERATE;
    }
    return audioAvailable ? null : AiDocumentationErrorCode.AUDIO_NO_LONGER_AVAILABLE;
};
/**
 * „Erneut versuchen" (Spec § 4.3): regenerate from the last good artefact —
 * `draft` when only the draft failed, `transcript` when the transcription
 * failed and the audio is still there. Null when nothing failed or nothing
 * can be retried (a failed normalization: discard and record again).
 */
export const retryModeAfterFailure = (state) => {
    if (state.dictationStatus === DictationStatus.TRANSCRIBED &&
        state.latestDraftStatus === AiDraftStatus.FAILED) {
        return regenerationBlocker(state, RegenerationMode.DRAFT) === null
            ? RegenerationMode.DRAFT
            : null;
    }
    if (state.dictationStatus === DictationStatus.FAILED) {
        return regenerationBlocker(state, RegenerationMode.TRANSCRIPT) === null
            ? RegenerationMode.TRANSCRIPT
            : null;
    }
    return null;
};
/**
 * Why a saved `AI_ASSISTED` entry cannot be regenerated now (Spec § 3.2-4),
 * or null when it can. The entry itself must be live — a manual correction
 * (rule 3.2-4) or a later save struck it out — and its dictation `SAVED`;
 * a post-save run still in flight is never overtaken. A finished one (ready,
 * failed or dropped) may be replaced by a new run.
 */
export const postSaveRegenerationBlocker = (state) => {
    if (!state.itemIsLive || state.dictationStatus !== DictationStatus.SAVED) {
        return AiDocumentationErrorCode.DRAFT_SUPERSEDED;
    }
    if (state.latestDraftStatus !== null && IN_FLIGHT_DRAFT.has(state.latestDraftStatus)) {
        return AiDocumentationErrorCode.RUN_IN_PROGRESS;
    }
    return null;
};
/**
 * Who may open the review of a dictation and save or drop its draft (Spec § 7
 * as decided in S6): before the first save only its author — the draft is
 * their unreviewed work; once `SAVED`, the whole practice, because the entry
 * and its transcript are practice records already (provenance) and a
 * colleague may regenerate it. The server enforces the same through CASL.
 */
export const canReviewDictation = (state) => state.isOwn || state.dictationStatus === DictationStatus.SAVED;
//# sourceMappingURL=regeneration.js.map